import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { 
  FeedbackData,
  CreateFeedbackRequest,
  FeedbackResponse
} from '../types/feedback';

export class UserFeedbackService extends BaseService {
  constructor() {
    super('UserFeedbackService', {
      enableCircuitBreaker: true,
      enablePerformanceTracking: true,
      enableRetry: true,
      timeout: 30000,
      maxRetryAttempts: 3,
      retryBaseDelay: 1000,
      failureThreshold: 5
    });
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retry attempt ${attempt} for ${operationName}`, {
      error: error.message,
      delay,
      attempt
    });
  }

  /**
   * Create app-level feedback
   */
  async createAppFeedback(feedbackData: FeedbackData): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      const request: CreateFeedbackRequest = {
        feedbackData
      };
      
      const endpoint = '/api/feedback/app';
      
      return await apiService.post<FeedbackResponse>(endpoint, request);
    }, 'createAppFeedback');
  }

  /**
   * Update app-level feedback
   */
  async updateAppFeedback(feedbackData: FeedbackData): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      const request: CreateFeedbackRequest = {
        feedbackData
      };
      
      const endpoint = '/api/feedback/app';
      
      return await apiService.put<FeedbackResponse>(endpoint, request);
    }, 'updateAppFeedback');
  }

  /**
   * Get app-level feedback
   */
  async getAppFeedback(): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      const endpoint = '/api/feedback/app';
      
      return await apiService.get<FeedbackResponse>(endpoint);
    }, 'getAppFeedback');
  }

  /**
   * Create session-level feedback
   */
  async createSessionFeedback(sessionId: string, feedbackData: FeedbackData): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      const request: CreateFeedbackRequest = {
        feedbackData
      };
      
      const endpoint = `/api/feedback/session/${encodeURIComponent(sessionId)}`;
      
      return await apiService.post<FeedbackResponse>(endpoint, request);
    }, 'createSessionFeedback');
  }

  /**
   * Update session-level feedback
   */
  async updateSessionFeedback(sessionId: string, feedbackData: FeedbackData): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      const request: CreateFeedbackRequest = {
        feedbackData
      };
      
      const endpoint = `/api/feedback/session/${encodeURIComponent(sessionId)}`;
      
      return await apiService.put<FeedbackResponse>(endpoint, request);
    }, 'updateSessionFeedback');
  }

  /**
   * Get session-level feedback
   */
  async getSessionFeedback(sessionId: string): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      const endpoint = `/api/feedback/session/${encodeURIComponent(sessionId)}`;
      
      return await apiService.get<FeedbackResponse>(endpoint);
    }, 'getSessionFeedback');
  }

  /**
   * Create or update app feedback (convenience method)
   */
  async submitAppFeedback(feedbackData: FeedbackData): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      // Try to get existing feedback first
      try {
        const existing = await this.getAppFeedback();
        if (existing.success && existing.feedback) {
          // Update existing feedback
          return await this.updateAppFeedback(feedbackData);
        }
      } catch (error) {
        // If getting feedback fails, try to create new feedback
        this.logDebug('Failed to get existing app feedback, will create new', { error });
      }
      
      // Create new feedback
      return await this.createAppFeedback(feedbackData);
    }, 'submitAppFeedback');
  }

  /**
   * Create or update session feedback (convenience method)
   */
  async submitSessionFeedback(sessionId: string, feedbackData: FeedbackData): Promise<FeedbackResponse> {
    return this.executeOperation(async () => {
      // Try to get existing feedback first
      try {
        const existing = await this.getSessionFeedback(sessionId);
        if (existing.success && existing.feedback) {
          // Update existing feedback
          return await this.updateSessionFeedback(sessionId, feedbackData);
        }
      } catch (error) {
        // If getting feedback fails, try to create new feedback
        this.logDebug('Failed to get existing session feedback, will create new', { error, sessionId });
      }
      
      // Create new feedback
      return await this.createSessionFeedback(sessionId, feedbackData);
    }, 'submitSessionFeedback');
  }
}

// Export singleton instance
export const userFeedbackService = new UserFeedbackService();
